import React, {FC, useEffect, useState} from "react";
import {Alert, Checkbox, Modal, Spin, Typography} from "antd";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import {gold} from "@ant-design/colors";
import i18n from "../../../services/i18n";
import {useAccountForEmailQuery} from "../../../generated/graphql";
import {roleLabel} from "./roleLabel";

export type LinkAccountConfirmModalProps = {
    open: boolean;
    /** E-Mail-Adresse, die bereits zu einem Zugang gehört. */
    targetEmail: string;
    /** Klarname des Buchers, der verknüpft werden soll. */
    bookerName: string;
    /** Bisheriger Online-Zugang des Buchers; nur gesetzt, wenn er sich von `targetEmail` unterscheidet. */
    previousEmail?: string | null;
    /** Der gerade bearbeitete Bucher: zählt bei „schon verknüpft“ nicht mit. */
    excludeBookerId?: string;
    /** Merger/Review-Queue: der Gewinner übernimmt Adresse und Zugang des Verlierers. */
    variant?: "link" | "merge";
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Dialog D1 „Zugang verknüpfen“ (docs/plans/2026-09-16-bucher-mitarbeiter-konto-verknuepfen.md, PR-3).
 *
 * Die Verknüpfung gibt einer Person Zugriff auf Kundendaten und macht das Passwort des
 * Mitarbeiter-Logins zum Kundenpasswort. Der Admin bestätigt deshalb mit beiden Klarnamen vor
 * Augen und einer Pflicht-Checkbox; ohne Checkbox ist Bestätigen unmöglich. Ist das Konto höher
 * berechtigt als der Aufrufer, gibt es keinen Bestätigen-Knopf.
 */
export const LinkAccountConfirmModal: FC<LinkAccountConfirmModalProps> = ({open, targetEmail, bookerName, previousEmail, excludeBookerId, variant = "link", onConfirm, onCancel}) => {
    const [acknowledged, setAcknowledged] = useState(false);
    const texts = i18n.containers.customers.LinkAccountConfirmModal;

    // Checkbox zurücksetzen, wenn der Dialog frisch geöffnet wird.
    useEffect(() => {
        if (open) {
            setAcknowledged(false);
        }
    }, [open, targetEmail]);

    const {data, loading, error} = useAccountForEmailQuery({
        variables: {email: targetEmail, excludeBookerId},
        skip: !open || targetEmail === "",
        fetchPolicy: "network-only",
    });

    const account = data?.accountForEmail;
    const mayConfirm = Boolean(account?.callerMayLink);
    // Altbestand: staff-Zeile, aber nur die EndUser-Rolle (allowLogin=false) → generisches Label.
    const staffRoles = (account?.roles ?? []).filter((role) => role !== "EndUser").map(roleLabel).join(", ") || (account?.isStaffAccount ? roleLabel("Staff") : "");
    const showPreviousAccess = Boolean(previousEmail) && previousEmail?.trim().toLowerCase() !== targetEmail.trim().toLowerCase();
    // Verschachtelte i18n-Aufrufe als Argument vermeiden: Werte vorher auflösen.
    const displayName = account?.displayName ?? targetEmail;
    const intro = staffRoles === "" ? texts.introNoRoles({targetEmail, displayName}) : texts.intro({targetEmail, displayName, roles: staffRoles});
    const lead = variant === "merge" ? texts.consequenceLeadMerge({bookerName}) : texts.consequenceLead({bookerName});
    const previousAccessText = texts.previousAccessText({previousEmail: previousEmail ?? ""});

    return (
        <Modal
            open={open}
            title={
                <span>
                    <ExclamationCircleOutlined style={{color: gold[5], marginRight: 8}} />
                    {texts.title()}
                </span>
            }
            width={640}
            onOk={() => {
                if (acknowledged && mayConfirm) onConfirm();
            }}
            onCancel={onCancel}
            okText={texts.confirm()}
            cancelText={mayConfirm ? texts.cancel() : texts.close()}
            okButtonProps={{
                disabled: !acknowledged || loading || !mayConfirm,
                style: mayConfirm ? undefined : {display: "none"},
                // @ts-expect-error data-Attribute sind in ButtonProps nicht typisiert, landen aber im DOM
                "data-testid": "link-account-confirm",
            }}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
        >
            {loading && (
                <div style={{textAlign: "center", padding: 20}}>
                    <Spin /> <span style={{marginLeft: 8}}>{texts.loading()}</span>
                </div>
            )}

            {error && <Alert type="error" showIcon message={texts.loadError()} description={error.message} style={{marginBottom: 12}} />}

            {!loading && !error && data && !account && <Alert type="info" showIcon message={texts.notFound()} />}

            {account && (
                <div data-testid="link-account-body">
                    <Typography.Paragraph strong>{intro}</Typography.Paragraph>

                    {mayConfirm ? (
                        <>
                            <Typography.Paragraph>{lead}</Typography.Paragraph>
                            <ul style={{paddingLeft: 20}}>
                                <li>
                                    <strong>{texts.oneLoginTitle()}</strong> {texts.oneLoginText()}
                                </li>
                                <li>
                                    <strong>{texts.dataAccessTitle()}</strong> {texts.dataAccessText()}
                                </li>
                                <li>
                                    <strong>{texts.passwordTitle()}</strong> {texts.passwordText()}
                                </li>
                                {showPreviousAccess && (
                                    <li>
                                        <strong>{texts.previousAccessTitle()}</strong> {previousAccessText}
                                    </li>
                                )}
                            </ul>
                            <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} data-testid="link-account-ack">
                                {texts.acknowledge()}
                            </Checkbox>
                        </>
                    ) : (
                        <Alert type="warning" showIcon message={texts.rankTooHighTitle()} description={texts.rankTooHighText()} data-testid="link-account-rank-too-high" />
                    )}
                </div>
            )}
        </Modal>
    );
};

export default LinkAccountConfirmModal;
